export const PROGRAM_SOURCE_GET_CSV_MAX_ROWS = 5000;
export const PROGRAM_SOURCE_UPDATE_CSV_MAX_ROWS = 5000;
export const UPDATE = "update";

export enum PROGRAM_NAMES_ENUM {
    SELECTION_GAP_CLOSURE = "SelectionGapClosure",
    STARFISH = "Starfish",
    SOURCE_INSIGHTS = "SourceInsights",
}

export const PROGRAM_V2 = [
    PROGRAM_NAMES_ENUM.SELECTION_GAP_CLOSURE,
    PROGRAM_NAMES_ENUM.STARFISH,
    PROGRAM_NAMES_ENUM.SOURCE_INSIGHTS,
];

// Readme
const COMMON_INSTRUCTIONS = [
    {
        title:"In case of server errors",
        content: "Requests resulting in a server error may lead to partial success of the request, i.e., only a " +
            "subset of records will succeed the operation. The list of succeeded and failed records will be " +
            "captured in the response message."
    },
    {
        title: "For list based columns",
        content: "If not provided or set to null, all list based columns will be treated as empty lists."
    },
    {
        title: "Follow the JSON Schema",
        content: "Ensure that your request follows the Json schema. Failure to do so will result in the particular sourceId being discarded."
    },
    {
        title: "Source name and Marketplace Id updates",
        content: "These field are static and cannot be modified once a nomination is made. Please cut a SIM to SWO if a need arises for updating these."
    }
];

export const SMPO_USAGE_DOCUMENTATION = {
    title: "User Manual for SMPO teams",
    link: "https://quip-amazon.com/i2jWAD1AIONL/SMPO-SOP-to-update-program-Source-Config",
    body: "SMPO teams to follow the special instruction set and examples from this document"
}

export const GET_README_INSTRUCTIONS = [
    {
        title: "Select a Program",
        content: "Choose the program for which you want to fetch sources. Note that each request is limited to fetching sources for a single program."
    },
    {
        title: "Upload a CSV File",
        content: `Upload an un-encoded CSV file that contains the sourceIds. Ensure that the number of records in the file does not exceed ${PROGRAM_SOURCE_GET_CSV_MAX_ROWS}.`
    },
    {
        title: "Download the Outcome",
        content: "Once the processing is complete, you will be able to download the output file. This file will contain the configurations of the existing sourceIds from the provided list."
    }
];

export const GET_README_COLUMN_SCHEMA = [
    {name: "sourceIds", required: true, description: "Unique identifier for the source"}
];

export const GET_FILE_EXAMPLE = [
    {sourceIds: "1072681"},
    {sourceIds: "1001101"}
];

export const UPDATE_README_INSTRUCTIONS = [
    {
        title: "Select a Program",
        content: "Choose the program for which you want to update sources.Note that each request is limited to updating sources for a single program."
    },
    {
        title: "Upload a CSV File",
        content: "Upload an unencoded CSV file that contains the configurations that need to be updated."
    },
    {
        title: "Field Updates",
        content: "The update functionality supports updating one or more fields for each record simultaneously. To update a field, provide its corresponding operation field with a valid value."
    },
    {
        title: "Download the Outcome",
        content: "Once the processing is complete, you will be able to download the output file. This file will list the sourceIds that were successfully updated as well as those that could not be updated."
    },
    {
        title: "Unique Source IDs",
        content: "Ensure that a single CSV file does not contain the same sourceId more than once. Multiple fields can be updated in the same row for a given sourceId."
    },
    {
        title: "Default Operation Behavior",
        content: "If the operation column is not specified or is invalid for any field for a record, the field won't " +
            "be updated for that record."
    },
    {
        title: "Registration and re-registration of records",
        content: "Registration/Nominations of new records will be rejected here and can only be done by EDRI. " +
            "Please reach out to them for new nominations."
    },
    ...COMMON_INSTRUCTIONS
];

export const VALID_OPERATIONS_DESCRIPTION = {
    listBased: {
        title: "Valid List-Based Operations (for subPrograms):",
        operations: [
            {
                operation: "upsert",
                description: "Does nothing if an element to be added already exists, otherwise inserts it in the list."
            },
            {
                operation: "overwrite",
                description: "User input completely overwrites the existing value."
            }
        ]
    },
    jsonBased: {
        title: "Valid JSON-Based Operations (for sdoProductStrategy and sciProductStrategy):",
        operations: [
            {
                operation: "deepMerge",
                description: "A deep merge operation combines two JSON objects recursively. It traverses through all nested objects and arrays, merging their contents. When a key exists in both objects, the value from the second object overwrites the first, unless both values are objects, in which case they are merged recursively. This process ensures that nested structures are preserved and combined, rather than being overwritten entirely."
            },
            {
                operation: "plainMerge",
                description: "A plain merge operation combines two JSON objects by overwriting fields in the original object with those provided in the input object. This includes both top-level and nested fields. If a field exists in the input, it completely replaces the corresponding field in the original object, regardless of whether the field contains a nested object or a simple value. If it does not exist in the original object it is added."
            },
            {
                operation: "overwrite",
                description: "User input completely overwrites the existing value."
            }
        ]
    }
};

export const UPDATE_README_COLUMN_SCHEMA = [
    {
        name: "sourceId",
        required: true,
        description: "Unique identifier for the source"
    },
    {
        name: "subPrograms",
        required: false,
        description: "Comma separated list of subPrograms. Valid values: CRBHS, DAWN, ZUMA"
    },
    {
        name: "sdoProductStrategy",
        required: false,
        description: "SDO config for data delivery (Json)"
    },
    {
        name: "sciProductStrategy",
        required: false,
        description: "SCI config for data delivery (Json)"
    },
    {
        name: "sdoProductStrategyOperation",
        required: false,
        description: "Operation type for SDO strategy. See Valid JSON-Based Operations above"
    },
    {
        name: "sciProductStrategyOperation",
        required: false,
        description: "Operation type for SCI strategy. See Valid JSON-Based Operations above"
    },
    {
        name: "subProgramsOperation",
        required: false,
        description: "Operation type for subPrograms. See Valid List-Based Operations above"
    }
];

export const UPDATE_FILE_EXAMPLE = [
    {
        sourceId: "1",
        subPrograms:"",
        sdoProductStrategy: "",
        sciProductStrategy: "",
        sdoProductStrategyOperation: "",
        sciProductStrategyOperation: "",
        subProgramsOperation: ""
    },
    {
        sourceId: "2",
        subPrograms: "CRBHS",
        sdoProductStrategy: '{"grainOfExtraction":"browse"}',
        sciProductStrategy: '{"targetAmazonMarketplaceIds":[],"applications":["EmBHS"],"isSourceQualified":false,"matching":{"model":"epm1dot1"},"clustering":{"dedupeRequired":true},"test":true}',
        sdoProductStrategyOperation: "plainMerge",
        sciProductStrategyOperation: "plainMerge",
        subProgramsOperation:"upsert"
    },
    {
        sourceId: "3",
        subPrograms:"CRBHS,DAWN",
        sdoProductStrategy: '{"grainOfExtraction":"detail","customField":"value"}',
        sciProductStrategy: '{"targetAmazonMarketplaceIds":["A1F83G8C2ARO7P"],"applications":["EmBHS","DAWN"],"isSourceQualified":true}',
        sdoProductStrategyOperation: "deepMerge",
        sciProductStrategyOperation: "overwrite",
        subProgramsOperation: "overwrite"
    },
    {
        sourceId: "4",
        subPrograms:"ZUMA",
        sdoProductStrategy: '{"grainOfExtraction":"browse","newField":{"subField":"value"}}',
        sciProductStrategy: "",
        sdoProductStrategyOperation: "deepMerge",
        sciProductStrategyOperation: "",
        subProgramsOperation: "upsert"
    },
    {
        sourceId: "5",
        subPrograms:"",
        sdoProductStrategy: '{"settings":{"timeout":30}}',
        sciProductStrategy: '{"matching":{"model":"epm2dot0"},"clustering":{"dedupeRequired":false}}',
        sdoProductStrategyOperation: "plainMerge",
        sciProductStrategyOperation: "deepMerge",
        subProgramsOperation: ""
    }
];

// Sample csv file name
export const SAMPLE_FETCH_RECORDS_INPUT_FILE = "sample_fetch_records_input.csv";
export const SAMPLE_UPDATE_RECORDS_INPUT_FILE = "sample_update_records_input.csv";

// Sample csv files

export const SAMPLE_FETCH_RECORDS_INPUT = `sourceIds
1072681
1001101`;

export const SAMPLE_UPDATE_RECORDS_INPUT = `sourceId,subPrograms,sdoProductStrategy,sciProductStrategy,sdoProductStrategyOperation,sciProductStrategyOperation,subProgramsOperation
1,,,,,,
2,CRBHS,"{""grainOfExtraction"":""browse""}","{""targetAmazonMarketplaceIds"":[],""applications"":[""EmBHS""],""isSourceQualified"":false,""matching"":{""model"":""epm1dot1""},""clustering"":{""dedupeRequired"":true},""test"":true}",plainMerge,plainMerge,upsert
3,"CRBHS,DAWN","{""grainOfExtraction"":""detail"",""customField"":""value""}","{""targetAmazonMarketplaceIds"":[""A1F83G8C2ARO7P""],""applications"":[""EmBHS"",""DAWN""],""isSourceQualified"":true}",deepMerge,overwrite,overwrite
4,CRBHS,"{""grainOfExtraction"":""browse"",""newField"":{""subField"":""value""}}","",deepMerge,,upsert
5,,"{""settings"":{""timeout"":30}}","{""matching"":{""model"":""epm2dot0""},""clustering"":{""dedupeRequired"":false}}",plainMerge,deepMerge,`;

// Required columns
export const GET_RECORDS_REQUIRED_COLUMNS = ['sourceIds'] as const;
export const RESTRICTED_UPDATE_COLUMNS = ['sourceName', 'marketplaceIds','marketplaceIdsOperation'] as const;
export const UPDATE_RECORDS_REQUIRED_COLUMNS = ['sourceId'] as const;